import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ContactRole } from "../../../services/organization.service";
import { PricingStore } from "../reference-set-pricing/s25.event.pricing.store";
import { PaymentsStore } from "../reference-set-pricing/payments/s25.payments.store";
import { S25PaymentRequestComponent } from "../reference-set-pricing/payments/s25.payment.request.component";
import { EventPricingService } from "../reference-set-pricing/s25.event.pricing.service";
import { OrgInvoiceRecords } from "./org-invoice-records";
import { OrgSeededPricingService } from "./org-seeded-pricing.service";

/**
 * Writes to one of the organization's billing contacts from the Invoices tab.
 *
 * The compose form and its templates belong to `s25-ng-payment-request`, which is scoped to one
 * event's billing - but a contact is billed across the organization's events, so the message has to
 * name which invoice it is about. The picker projected into the panel is that choice, and changing
 * it re-points the scoped stores at the other invoice's event. Those stores are marked
 * compose-only, which keeps the pricing page's own reads out of it.
 *
 * The panel itself is in email-only mode: no amount, no processor, no payment logged.
 */
@TypeManagerDecorator("s25-ng-org-contact-email")
@Component({
    selector: "s25-ng-org-contact-email",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25PaymentRequestComponent],
    providers: [{ provide: EventPricingService, useClass: OrgSeededPricingService }, PricingStore, PaymentsStore],
    template: `
        <s25-ng-payment-request>
            <label class="contact-email-invoice">
                <span>Reference Invoice/Event</span>
                <select
                    class="cn-form__control"
                    [ngModel]="selectedInvoiceId()"
                    (ngModelChange)="selectedInvoiceId.set(+$event)"
                >
                    @for (option of options(); track option.id) {
                        <option [ngValue]="option.id">{{ option.label }}</option>
                    }
                </select>
            </label>
        </s25-ng-payment-request>
    `,
    styles: `
        .contact-email-invoice {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin-bottom: 1rem;
            font-weight: 600;
        }

        .contact-email-invoice .cn-form__control {
            font-weight: 400;
        }
    `,
})
export class S25OrgContactEmailComponent {
    private readonly pricingStore = inject(PricingStore);
    private readonly paymentsStore = inject(PaymentsStore);

    readonly orgId = input.required<number>();
    readonly contact = input.required<ContactRole>();
    readonly invoices = input.required<OrgInvoiceRecords.Record[]>();

    readonly closed = output<void>();

    protected readonly options = computed(() =>
        this.invoices().map((record) => ({
            id: record.id,
            label: `${record.invoiceNumber || record.invoiceName} — ${record.eventName}`,
        })),
    );

    protected readonly selectedInvoiceId = linkedSignal(() => {
        const invoices = this.invoices();
        const owed = invoices.find((record) => record.outstandingPaymentIds.length);
        return (owed ?? invoices[0])?.id;
    });

    private readonly selected = computed(() =>
        this.invoices().find((record) => record.id === this.selectedInvoiceId()),
    );

    private readonly hasOpened = signal(false);

    constructor() {
        // Set before anything can read it, so none of the page's own reads are ever started.
        this.pricingStore.setComposeOnly(true);

        effect(() => {
            const selected = this.selected();
            if (!selected) return;

            this.pricingStore.setEventId(selected.eventId);
            this.pricingStore.setEvBillId(selected.id);
            this.paymentsStore.setEventName(selected.eventName);
        });

        effect(() => {
            const orgId = this.orgId();
            const contactId = this.contact().itemId;

            untracked(() => {
                if (this.paymentsStore.requestOrgId()) return;
                this.paymentsStore.openContactEmail(orgId, contactId);
            });
        });

        effect(() => {
            if (!!this.paymentsStore.requestOrgId()) return untracked(() => this.hasOpened.set(true));
            if (untracked(this.hasOpened)) this.closed.emit();
        });
    }
}
